import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail, MessageSquare, Phone, Share2, Megaphone, Search, Users, AtSign, Globe, UserCheck } from 'lucide-react';
import Button from '../components/common/Button';

const products = [
  {
    id: 'email-marketing',
    name: 'Email Marketing',
    icon: <Mail className="w-12 h-12" />,
    description: 'AI-powered email campaigns with personalized content delivery',
    features: [
      'Smart personalization engine',
      'A/B testing capabilities',
      'Automated campaign optimization',
      'Advanced analytics and reporting',
      'Integration with major platforms'
    ],
    benefits: [
      'Increase open rates by up to 35%',
      'Improve click-through rates',
      'Boost conversion rates',
      'Save time with automation',
      'Make data-driven decisions'
    ]
  },
  {
    id: 'sms-marketing',
    name: 'SMS Marketing',
    icon: <MessageSquare className="w-12 h-12" />,
    description: 'Direct and instant communication with your customers through SMS',
    features: [
      'Two-way messaging',
      'Bulk SMS campaigns',
      'Automated responses',
      'Message scheduling',
      'Contact management'
    ],
    benefits: [
      '98% open rate',
      'Instant delivery',
      'Higher engagement rates',
      'Direct customer communication',
      'Improved customer service'
    ]
  },
  {
    id: 'whatsapp-marketing',
    name: 'WhatsApp Marketing',
    icon: <Phone className="w-12 h-12" />,
    description: 'Engage customers through WhatsApp Business API',
    features: [
      'Rich media messages',
      'Automated workflows',
      'Template messages',
      'Chat automation',
      'Analytics dashboard'
    ],
    benefits: [
      'Enhanced customer engagement',
      'Rich media capabilities',
      'Global reach',
      'Cost-effective communication',
      'Improved response rates'
    ]
  },
  {
    id: 'social-media',
    name: 'Social Media Marketing',
    icon: <Share2 className="w-12 h-12" />,
    description: 'Comprehensive social media management across all platforms',
    features: [
      'Content scheduling',
      'Analytics tracking',
      'Engagement monitoring',
      'Multi-platform management',
      'Team collaboration'
    ],
    benefits: [
      'Increased brand awareness',
      'Better audience engagement',
      'Time-saving automation',
      'Consistent brand presence',
      'Performance tracking'
    ]
  },
  {
    id: 'advertising',
    name: 'Online Advertising',
    icon: <Megaphone className="w-12 h-12" />,
    description: 'Strategic digital advertising campaigns across multiple platforms',
    features: [
      'Multi-channel campaigns',
      'Audience targeting',
      'Budget optimization',
      'Performance tracking',
      'A/B testing'
    ],
    benefits: [
      'Increased ROI',
      'Better targeting',
      'Real-time optimization',
      'Comprehensive reporting',
      'Cost efficiency'
    ]
  },
  {
    id: 'seo',
    name: 'SEO Services',
    icon: <Search className="w-12 h-12" />,
    description: 'Improve your search engine rankings with comprehensive SEO strategies',
    features: [
      'Keyword research',
      'On-page optimization',
      'Technical SEO',
      'Content strategy',
      'Link building'
    ],
    benefits: [
      'Higher search rankings',
      'Increased organic traffic',
      'Better user experience',
      'Long-term results',
      'Competitive advantage'
    ]
  },
  {
    id: 'kol',
    name: 'KOL Marketing',
    icon: <Users className="w-12 h-12" />,
    description: 'Connect with influential voices in your industry',
    features: [
      'Influencer discovery',
      'Campaign management',
      'Performance tracking',
      'Content collaboration',
      'ROI measurement'
    ],
    benefits: [
      'Authentic brand promotion',
      'Increased reach',
      'Higher engagement',
      'Brand credibility',
      'Target audience access'
    ]
  },
  {
    id: 'business-email',
    name: 'Business Email',
    icon: <AtSign className="w-12 h-12" />,
    description: 'Professional email hosting with advanced features',
    features: [
      'Custom domain emails',
      'Advanced security',
      'Large storage',
      'Spam protection',
      'Mobile access'
    ],
    benefits: [
      'Professional appearance',
      'Reliable service',
      'Enhanced security',
      'Team collaboration',
      'Business continuity'
    ]
  },
  {
    id: 'website-builder',
    name: 'Website Builder',
    icon: <Globe className="w-12 h-12" />,
    description: 'Create stunning websites with our easy-to-use builder',
    features: [
      'Drag-and-drop editor',
      'Responsive templates',
      'SEO tools',
      'Custom domains',
      'Analytics integration'
    ],
    benefits: [
      'Professional website',
      'No coding required',
      'Mobile optimization',
      'Fast loading speed',
      'Easy maintenance'
    ]
  },
  {
    id: 'crm',
    name: 'CRM System',
    icon: <UserCheck className="w-12 h-12" />,
    description: 'Manage customer relationships effectively',
    features: [
      'Contact management',
      'Sales pipeline',
      'Task automation',
      'Reporting tools',
      'Email integration'
    ],
    benefits: [
      'Better customer relationships',
      'Increased sales',
      'Improved efficiency',
      'Data-driven decisions',
      'Team collaboration'
    ]
  }
];

const ProductDetailsPage = () => {
  const location = useLocation();
  const hash = location.hash.replace('#', '');
  const targetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hash && targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [hash]);

  return (
    <div className="pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Our Products
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive solutions for all your digital marketing needs
          </p>
        </div>

        <div className="space-y-24">
          {products.map((product) => (
            <div
              key={product.id}
              id={product.id}
              ref={hash === product.id ? targetRef : null}
              className="scroll-mt-24"
            >
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-8 md:p-12">
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-8">
                    <div className="bg-blue-100 p-4 rounded-lg">
                      {React.cloneElement(product.icon, {
                        className: "text-blue-600"
                      })}
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">
                        {product.name}
                      </h2>
                      <p className="text-xl text-gray-600">
                        {product.description}
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-xl font-semibold mb-4">Key Features</h3>
                      <ul className="space-y-3">
                        {product.features.map((feature, index) => (
                          <li key={index} className="flex items-start">
                            <svg
                              className="h-6 w-6 text-green-500 mr-2"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold mb-4">Benefits</h3>
                      <ul className="space-y-3">
                        {product.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-start">
                            <svg
                              className="h-6 w-6 text-blue-500 mr-2"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-8 flex justify-center">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() => window.location.href = `/pricing#${product.id}`}
                    >
                      View Pricing
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsPage;